import * as zod from 'zod';

import { CartesianPose, ZERO_POSE } from '@sb/geometry';
import { CameraIntegration } from '@sb/integrations/types/cameraTypes';
import { Step } from '@sb/remote-control/types';
import {
  DEFAULT_REGION_OF_INTEREST,
  Detection3DPoseEstimationConfig,
  LocateMethod,
  RegionOfInterest,
  ResultOrdering,
} from '@sb/routine-runner';
import { CaptureConfig } from '@sb/routine-runner/Step/Capture/captureConfig';
import {
  DEFAULT_MAX_TILT_ANGLE,
  ProjectionMethod,
  RansacParams,
} from '@sb/routine-runner/Step/Locate/Arguments';

export namespace LocateStep {
  export const name = 'Locate';
  export const description = 'Locate via vision';
  export const librarySection = Step.LibrarySection.Vision;
  export const argumentKind = 'Locate';
  export const featureFlag = 'locateStep';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    // For camera-bot-cpp mode
    cameraEquipmentId: zod.string().optional(),
    // For non-camera-bot-cpp mode
    camera: CameraIntegration.optional(),
    regionOfInterest: RegionOfInterest.default(DEFAULT_REGION_OF_INTEREST),
    method: LocateMethod,
    positionListName: zod.string().default('Locate step results'),
    // The operator-facing control was removed; the count is fixed at 50 (the
    // same cap the skill wizard writes and runLocate falls back to). An
    // already-saved step keeps whatever it persisted.
    resultsLimit: zod.number().default(50),
    resultOrdering: ResultOrdering.optional(),
    transform: CartesianPose.default(ZERO_POSE),
    // Grasp frame the `transform` offset rides in (object / plane / world).
    // Preview-only: `toRoutineRunner` does not forward it (the standalone
    // Locate step keeps its plane-relative transform), but
    // `getLocateArgsFromLocateStepArgs` threads it into the wizard's
    // live-preview locate call so Results reflect the chosen frame.
    graspFrame: zod.enum(['object', 'plane', 'world']).optional(),
    planeID: zod.string().optional(),
    objectHeight: zod.number().default(0),
    projectionMethod: ProjectionMethod,
    ransacParams: RansacParams.optional(),
    maxTiltAngle: zod
      .number()
      .min(1)
      .max(90)
      .optional()
      .default(DEFAULT_MAX_TILT_ANGLE),
    // DetectedPlane only — see the matching fields on the routine-runner
    // Locate Arguments schema.
    minInlierRatio: zod.number().min(0).max(1).optional(),
    fallbackToTopDown: zod.boolean().optional(),
    disablePlaneOrientation: zod.boolean().optional().default(false),
    planeOpacity: zod.number().optional().default(0.5),
    detection3dConfig: Detection3DPoseEstimationConfig.optional(),
    // Canonical orientation frame persisted at teach time. Preview-only, like
    // `graspFrame` above: `toRoutineRunner` does not forward
    // it, but `getLocateArgsFromLocateStepArgs` threads it into the wizard's
    // live-preview locate call so an already-saved skill's reference rides into
    // the plane-projection request when editing. Null when never taught.
    orientationReference: zod
      .object({
        referenceContour: zod.array(zod.array(zod.number())),
      })
      .nullable()
      .optional(),
    captureConfig: CaptureConfig.optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'Locate',
      args: {
        camera: args.camera,
        cameraEquipmentId: args.cameraEquipmentId,
        regionOfInterest: args.regionOfInterest,
        method: args.method,
        positionListID: stepData.id,
        filters: {
          resultsLimit: args.resultsLimit,
        },
        transform: args.transform,
        planeID: args.planeID,
        objectHeight: args.objectHeight,
        projectionMethod: args.projectionMethod,
        ransacParams: args.ransacParams,
        maxTiltAngle: args.maxTiltAngle,
        minInlierRatio: args.minInlierRatio,
        fallbackToTopDown: args.fallbackToTopDown,
        detection3dConfig: args.detection3dConfig,
        disablePlaneOrientation: args.disablePlaneOrientation,
        resultOrdering: args.resultOrdering,
        captureConfig: args.captureConfig,
      },
    };
  };
}

LocateStep satisfies Step.StepKindInfo;
